// empty-state.tsx — the ONE first-run empty state shared by the three
// management tabs (Campaigns / Audiences / Templates).
//
// Why one component: the three tabs used to each invent their own "nothing
// here" treatment (a bordered card with a 16px headline on Campaigns, a bare
// 26px grey glyph + one line of 13px text on Audiences and Templates), so the
// first thing a brand-new account saw was three different-looking dead ends.
// This renders the same shape everywhere: one big tinted glyph, one headline,
// one sentence, and exactly ONE obvious primary button.
//
// Rule that goes with it (enforced at each call site, not here): when a
// collection is genuinely empty — no rows at all, not "no rows match" — the
// tab hides its whole toolbar (search box, filter pills, sort/density
// toggles). There is nothing to search or filter, and every extra control is
// one more thing between the user and the single button they should press.

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Plus } from 'lucide-react';

export interface FirstRunEmptyStateProps {
  /** Big glyph in the tinted tile. */
  icon: LucideIcon;
  headline: string;
  /** One sentence. What this thing is / what pressing the button gets you. */
  body: string;
  ctaLabel: string;
  onCta: () => void;
  ctaDisabled?: boolean;
  /** Optional low-emphasis alternative (e.g. "Start from a template"). */
  secondary?: { label: string; icon?: LucideIcon; onSelect: () => void };
  /** Optional tutorial link (a GuideVideoButton) — renders nothing until its video exists. */
  video?: React.ReactNode;
}

export const FirstRunEmptyState: React.FC<FirstRunEmptyStateProps> = ({
  icon: Icon,
  headline,
  body,
  ctaLabel,
  onCta,
  ctaDisabled,
  secondary,
  video,
}) => {
  const SecondaryIcon = secondary?.icon;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '72px 24px 56px',
        margin: '0 auto',
        maxWidth: 460,
      }}
    >
      <div
        style={{
          width: 68,
          height: 68,
          borderRadius: 20,
          background: 'var(--surface-3)',
          border: '1px solid var(--line)',
          color: 'var(--bt-blue)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 18,
        }}
      >
        <Icon size={30} strokeWidth={1.7} />
      </div>

      <h2 style={{ fontSize: 19, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em', margin: 0 }}>
        {headline}
      </h2>
      <p style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--muted)', margin: '8px 0 0', maxWidth: 360 }}>
        {body}
      </p>

      {/* The single obvious next click. Deliberately oversized relative to the
          toolbar buttons — on an empty tab it is the only thing to press. */}
      <button
        type="button"
        className="btn primary"
        onClick={onCta}
        disabled={ctaDisabled}
        style={{ marginTop: 22, padding: '10px 20px', fontSize: 13.5, gap: 8 }}
      >
        <Plus size={16} /> {ctaLabel}
      </button>

      {secondary && (
        <button
          type="button"
          onClick={secondary.onSelect}
          style={{
            appearance: 'none',
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            marginTop: 12,
            padding: '6px 8px',
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--muted)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {SecondaryIcon && <SecondaryIcon size={14} />} {secondary.label}
        </button>
      )}
      {video && <div style={{ marginTop: 6 }}>{video}</div>}
    </div>
  );
};

export default FirstRunEmptyState;
